<template>
    <app-layout title="Products">
        <page-header>
            <template #title>Stock Log</template>
        </page-header>

        <reports-navigation />

        <div class="px-8 mt-2">
            <form class="flex gap-x-2 items-center" @submit.prevent="handleSubmit">
                <!-- Search / Filters -->
                <jet-input type="text" class="w-4/12 h-7 text-base" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>

                <div class="w-3/12">
                    <datepicker inputClassName="dp-custom-input"
                        @update:modelValue="handleUpdateDateRange"
                        v-model="date"
                        :is24="false"
                        :enableTimePicker="false"
                        :autoApply="true"
                        range
                        twoCalendars
                        placeholder="Select Date Range"
                        :format="formatDateRange"
                        weekStart=0
                    />
                </div>

                <jet-tertiary-button type="button" class="ml-4 py-1 h-7 self-start" @click="handleReset">
                    Reset
                </jet-tertiary-button>
            </form>
        </div>

        <div class="px-8 pt-2 pb-4">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="text-left whitespace-nowrap">Item #</th>
                            <th class="text-left">Product</th>
                            <th class="text-left whitespace-nowrap">Order #</th>
                            <th class="text-left">User</th>
                            <th class="text-left">Description</th>
                            <th class="text-left">Previous Quantity</th>
                            <th class="text-left">New Quantity</th>
                            <th class="text-left">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items.data" :key="item.id">
                            <td>
                                <span class="block px-2 py-1">{{ item.product_id }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">
                                    {{ item.product.title }}
                                    <span v-if="item.product.size"> - {{ item.product.size }}</span>
                                </span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ item.order_id }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ item?.user?.name }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ item.type }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ item.stock_old }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ item.stock_new }}</span>
                            </td>
                            <td>
                                <span class="block px-2 py-1">{{ formatTimestamp(item.created_at) }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <pagination class="inline-flex" :links="items.links" />
        </div>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import JetInput from '@/Jetstream/Input';
import Pagination from '@/Jetstream/Pagination';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";
import ReportsNavigation from '@/Pages/Reports/ReportsNavigation';

import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {
        const form = useForm('search', {
            search: props.filters.search,
            from: props.filters.from,
            to: props.filters.to,
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetInput,
        JetTertiaryButton,
        Pagination,
        Datepicker,
        ReportsNavigation
    },

    props: ['items', 'filters'],

    data() {
        return {
            date: null,
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.get(route('reports.stock.index'), { preserveState: true });
        }, 250),

        handleReset () {
            this.form.search = '';
            this.form.from = '';
            this.form.to = '';
            this.date = null;
            this.handleSubmit();
        },

        formatTimestamp(timestamp) {
            return DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_MED);
        },

        handleUpdateDateRange(dateRange) {
            this.form.from = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[0]).toISODate() : null;
            this.form.to = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[1]).toISODate() : null;
            this.handleSubmit();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end) {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },
    },

    mounted() {
        this.setDateRange();
    },
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>

<style>
.dp-custom-input {
    @apply h-7;
}
</style>
